// 剣舞のアルレ版の土台の踊り = ワルツ (2026-10-25・開発版 ?bladeArle=1)。
//   作者 (答え blade-100): 土台の踊り「ワルツダンス」。「忠実に再現しようとしているせいで逆に何しているかわからないってパターンは今までも結構あった」
//   ・待機と歩きをワルツの組んだ構え (m1025_w_1) と踏み出し (w_2) にする。立ち姿 (idle) へは戻らない。
//   ・どの技も、締めは両腕を広げた同じ構え (w_3) を通ってワルツの構えへ戻る (立ち姿 m1020_waltz_c6・剣の差し替え swap_draw を使わない)。
//   ・長い連は、締めの一撃の前で片脚を上げた見得 (w_4) で 1.5 秒止まる。
//   ・連のつなぎは 3拍子 (1拍 450ms) に乗せる = つなぎの間と溜めの合計を 150ms の倍数へ丸める。
//   ・暗器投げは投げ専用の絵 (振りかぶり t_4・放つ t_2)、旋回突きの④の前の溜め (p4_6c)・回る連の出だし (r_1b) は描き直した絵にする。
import { WALK_DISCRETE_SNAP, isBossTimelineCrossfade, setBossIdleOverride, setBossTimelineCrossfade, setWalkPairOverride } from './sprites'
import { BOSS_ATTACK_TIMELINES, type BossAttackTimeline } from './boss_pose_timeline'
import { THROW_REARM_AT, setBladeThrowHandGrip, type ThrowHandGrip } from './blade_dancer_throw_knife'
import type { BossString, SpriteKey } from './types'

const BOSS_ID = 'blade-dancer'
export const WALTZ_HOLD = 'm1025_w_1'    // 組んだ構え (待機)
export const WALTZ_STEP = 'm1025_w_2'    // 踏み出し (歩きの相方)
export const WALTZ_CLOSE = 'm1025_w_3'   // 両腕を広げた締め
export const WALTZ_MIE = 'm1025_w_4'     // 片脚を上げた見得
export const CLAP_KEYS = ['m1029_clap_in', 'm1025_k_1', 'm1025_k_2'] as const
//   崩れの二段 (入り・保つ) は、並びにも状態の絵にも出ないのでここで読む (幹の差し替え = src/blade_dancer_arle.ts)。
export const STAGGER_KEYS = ['staggered_enter', 'staggered_hold'] as const

const STAND = 'm1020_waltz_c6'
const SWAP_DRAW = 'swap_draw'
export const WALTZ_BEAT_MS = 450
const SUB_BEAT_MS = 150
export const WALTZ_MIE_MS = 1500
/** 締めの一撃の前で見得を切る連。 */
export const WALTZ_MIE_STRINGS = ['arle-a-pull-grand', 'arle-a-quick-grand', 'arle-throw-pull-grand', 'arle-a-quick-spin-delta']

export const THROW_WINDUP = 'm1026_t_4'      // 暗器投げの振りかぶり (投げる手前の腕で小刀を肩の後ろへ引く)
export { PIROUETTE_COIL } from './blade_dancer_pirouette'  // 旋回突きの突きの前の溜め (低くかがんで2本を腰へ引き寄せる)
/** t_4 の頭上の拳 (画面 px・足元から前, 上)。小刀は拳から後ろ上へ向ける (右向きの時の rad)。 */
export const THROW_WINDUP_GRIP: ThrowHandGrip = { fwd: 10, up: 163, rad: -Math.PI * 0.8 }

/** 技ごとに描き直した絵へ差し替える (phase ごとに分けたい物は関数で)。 */
const REDRAWN: Record<string, (image: string, phase: string) => string> = {
  // 暗器投げ (10-26 作者「投げている腕と…実際に投げて前に突き出した絵が逆の腕」「投げた後、なぜかまた構えのような形をして、最後決めポーズ」):
  //   振りかぶり (t_4) を放つ絵 (t_2) と同じ手前の腕にし、1本目も2本目も同じ振りかぶり → 放つ。投げ終えたら締め (w_3) を通さずワルツの構えへ戻る。
  bladeKnifeThrow: (im) => im === 'm1025_c_3' ? THROW_WINDUP : im === 'm1025_c_2' ? 'm1025_t_2' : im === WALTZ_CLOSE ? 'idle' : im,
  // 旋回突きの描き直した絵 (p4_6c) は、5連の並びに直接書いた (src/blade_dancer_pirouette.ts の BLADE_PIROUETTE_ARLE_TIMELINE)。
  bladeSpinChain: (im) => im === 'm1025_r_1' ? 'm1025_r_1b' : im,
}

/** つなぎの間を、間と溜めの合計が 150ms の倍数になるまで延ばす (3拍子に乗せる)。 */
export function roundGapToBeat(gapMs: number, chargeMs: number): number {
  const total = Math.ceil((gapMs + chargeMs) / SUB_BEAT_MS) * SUB_BEAT_MS
  return total - chargeMs
}

/** 締め (recovery) ごと差し替える技。 */
const RECOVERY: Record<string, BossAttackTimeline> = {
  // 暗器投げ (10-27 作者「あっさりしすぎ」「絵が切り替わってる感がすごい」):
  //   放った腕を伸ばしたまま少し前へ流れて刃の行方を見送る → 空いた手に剣が戻り (t_3・手もとが光る)、相手へ切っ先を向けて残心
  //   → 切っ先を前へ向けたままワルツの構え (w_1 も切っ先は前) へ引く。放つ t_2 と t_3 は同じ踏み込みで、手に剣があるかだけが違う。
  bladeKnifeThrow: [
    { phase: 'recovery', at: 0, image: 'm1025_t_2', hold: true, tag: 'followThrough', ease: 'easeOut' },
    { phase: 'recovery', at: 0.28, image: 'm1025_t_2', hold: true, offset: { x: 10 } },
    { phase: 'recovery', at: THROW_REARM_AT, image: 'm1025_t_3', hold: true, tag: 'recover', offset: { x: 10 }, ease: 'easeInOut' },
    { phase: 'recovery', at: 0.66, image: 'm1025_t_3', hold: true, offset: { x: -4 } },
    { phase: 'recovery', at: 0.72, image: 'idle', hold: true, offset: { x: -4, y: 3 }, ease: 'easeOut' },
    { phase: 'recovery', at: 0.9, image: 'idle', hold: true },
  ],
}

// 10-28 作者「全体的に感じるのが動きがちょっと飛んでるところがあるから、次はそこをスムーズにしてほしい」。
//   アルレ版は絵を溶かさない (下の setBossTimelineCrossfade) ので、飛びは動き (offset/rot/scale の補間) で埋める。
//   ・技の出だし: 立ち姿の手前 (s2) を一瞬はさむと、ワルツの構え → s2 → 合図の構え と2回替わってちらつく。構えのまま合図へ入る。
const SETTLE = 'm1020_waltz_s2'

// 10-28 夕 作者「idleに戻る際にどのわざも複数の絵を瞬間的に切り替えていると思うんだけど、それが何してるかわからないと言うかゴチャゴチャしてる感じがする」。
//   戻りは どの技も同じ形に絞る (手本 = 配布版の五連・三連: 意味のある絵を長めに見せ、あとは立ち姿へ):
//   振り抜いた絵 (体が流れて止まる) → [息をつく絵があれば1枚] → 両腕を広げる締め w_3 (少し沈んだ所から伸び上がる) → ワルツの構え。
//   どの絵も 0.2 秒ほど以上見せる。立ち直りの橋渡しの絵 (g2・s1・s2・b2) を短く何枚も挟まない。
/** 振り抜きと締めの間に残す、意味のある1枚 (技ごと)。 */
const CLOSE_KEEP: Record<string, string> = {
  crescendo: 'm1020_v6_g3',    // 肩で息をつく
  bladeGrand: 'm1020_v6_g3',
}
const CLOSE_RISE = { x: 1.02, y: 0.93 }   // 締めの入りは少し沈んだ背丈から

export function tidyRecovery(id: string, tl: BossAttackTimeline, keep = CLOSE_KEEP[id]): BossAttackTimeline {
  const head = tl.find(k => k.phase === 'recovery')
  if (!head) return tl
  const close = keep ? 0.55 : 0.36
  const keys: BossAttackTimeline = [{ ...head, at: 0, hold: true }]
  if (keep) keys.push({ phase: 'recovery', at: 0.26, image: keep, hold: true, ease: 'easeOut' })
  keys.push(
    { phase: 'recovery', at: close, image: WALTZ_CLOSE, hold: true, tag: 'recover', scale: CLOSE_RISE, ease: 'easeOut' },
    { phase: 'recovery', at: close + 0.14, image: WALTZ_CLOSE, hold: true, ease: 'easeOut' },
    { phase: 'recovery', at: 0.87, image: 'idle', hold: true },
  )
  return [...tl.filter(k => k.phase !== 'recovery'), ...keys]
}

function waltzTimeline(id: string, tl: BossAttackTimeline): BossAttackTimeline {
  const redraw = REDRAWN[id]
  const recovery = RECOVERY[id]
  tl = recovery ? [...tl.filter(k => k.phase !== 'recovery'), ...recovery] : tidyRecovery(id, tl)
  let lead = true   // 合図の頭から続く s2 (溶かし用の複製キーも含む) だけを構えに替える
  return tl.map(k => {
    let image = k.image === STAND || k.image === SWAP_DRAW ? WALTZ_CLOSE : k.image
    if (k.phase !== 'telegraph' || image !== SETTLE) lead = false
    if (lead) image = WALTZ_HOLD
    if (redraw) image = redraw(image, k.phase)
    return image === k.image ? k : { ...k, image }
  })
}

/** 連をワルツの拍に乗せ、長い連の締めの一撃の前に見得を入れる。 */
export function waltzStrings(strings: BossString[]): BossString[] {
  return strings.map(s => {
    const mie = WALTZ_MIE_STRINGS.includes(s.id)
    return {
      ...s,
      beats: s.beats.map((b, i) => {
        if (i === 0) return b
        const charge = b.chargeOverrideMs ?? 0
        const last = i === s.beats.length - 1
        if (mie && last) return { ...b, gapMs: roundGapToBeat(WALTZ_MIE_MS, charge), gapImage: WALTZ_MIE }
        return { ...b, gapMs: roundGapToBeat(b.gapMs ?? 0, charge) }
      }),
    }
  })
}

let snap: Record<string, BossAttackTimeline> | null = null
let snapWalkSnap = false
let snapCrossfade = false

/** アルレ版の技の並びをワルツにする (useBladeDancerArle の最後に呼ぶ)。attackIds = アルレ版で出る技。 */
export function useBladeDancerWaltz(attackIds: string[]): void {
  const tls = BOSS_ATTACK_TIMELINES[BOSS_ID] ?? {}
  if (!snap) {
    snap = {}
    for (const id of attackIds) if (tls[id]) snap[id] = tls[id]!
    snapWalkSnap = WALK_DISCRETE_SNAP.has(BOSS_ID)
    snapCrossfade = isBossTimelineCrossfade(BOSS_ID)
  }
  const next = { ...tls }
  for (const id of Object.keys(snap)) next[id] = waltzTimeline(id, snap[id]!)
  BOSS_ATTACK_TIMELINES[BOSS_ID] = next
  setBossIdleOverride(BOSS_ID, WALTZ_HOLD as unknown as SpriteKey, [WALTZ_STEP, WALTZ_MIE, ...CLAP_KEYS, ...STAGGER_KEYS] as unknown as SpriteKey[])
  setWalkPairOverride(BOSS_ID, [WALTZ_HOLD, WALTZ_STEP] as unknown as [SpriteKey, SpriteKey])
  WALK_DISCRETE_SNAP.add(BOSS_ID)   // 形の違う2枚を溶かさず、パッと踏み替える
  // 絵の替わり目を溶かさない (10-27 作者「フェード的な切り替えが『絵がちらついている』ようにみえるからやめてほしい」)。
  //   配布版の剣舞 (V6) は溶かすまま。替わり目の飛びは、技ごとに間の絵か動き (offset/rot) で埋める。
  setBossTimelineCrossfade(BOSS_ID, false)
  setBladeThrowHandGrip(THROW_WINDUP_GRIP)
}

/** 外す (テストの後始末用)。 */
export function restoreBladeDancerWaltz(): void {
  if (!snap) return
  const tls = BOSS_ATTACK_TIMELINES[BOSS_ID]
  if (tls) for (const id of Object.keys(snap)) tls[id] = snap[id]!
  setBossIdleOverride(BOSS_ID, null)
  setWalkPairOverride(BOSS_ID, null)
  if (!snapWalkSnap) WALK_DISCRETE_SNAP.delete(BOSS_ID)
  setBossTimelineCrossfade(BOSS_ID, snapCrossfade)
  setBladeThrowHandGrip(null)
  snap = null
}
